/**
 * @zendir/ui - Pagination Component
 *
 * Pagination following Astro UX Design System.
 *
 * @example
 * ```tsx
 * <Pagination
 *   currentPage={page}
 *   totalPages={10}
 *   onChange={setPage}
 * />
 * ```
 */

import React, { memo, useMemo } from "react";
import { useTheme } from "../../theme";
import { classNames } from "../../utils";

export interface PaginationProps {
  /** Current page (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Change handler */
  onChange: (page: number) => void;
  /** Number of page buttons to show */
  siblingCount?: number;
  /** Show first/last buttons */
  showFirstLast?: boolean;
  /** Show prev/next buttons */
  showPrevNext?: boolean;
  /** Size variant */
  size?: "small" | "medium";
  /**
   * Display mode.
   * - `'buttons'` — numbered page buttons with ellipsis (default).
   * - `'text'`    — compact "Page X of Y" label with prev/next arrows.
   */
  displayMode?: "buttons" | "text";
  /** Disabled state */
  disabled?: boolean;
  /** Custom className */
  className?: string;
}

export const Pagination = memo(function Pagination({
  currentPage,
  totalPages,
  onChange,
  siblingCount = 1,
  showFirstLast = true,
  showPrevNext = true,
  size = "medium",
  displayMode = "buttons",
  disabled = false,
  className = "",
}: PaginationProps): React.ReactElement {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";

  const sizeConfig = {
    small: { buttonSize: "28px", fontSize: tokens.typography.fontSize.xxs },
    medium: { buttonSize: "34px", fontSize: tokens.typography.fontSize.xs },
  };
  const config = sizeConfig[size];

  // Calculate visible page numbers
  const pageNumbers = useMemo(() => {
    const range: (number | "ellipsis")[] = [];
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

    const showLeftEllipsis = leftSiblingIndex > 2;
    const showRightEllipsis = rightSiblingIndex < totalPages - 1;

    if (!showLeftEllipsis && showRightEllipsis) {
      const leftRange = Array.from(
        { length: 3 + 2 * siblingCount },
        (_, i) => i + 1,
      );
      range.push(...leftRange, "ellipsis", totalPages);
    } else if (showLeftEllipsis && !showRightEllipsis) {
      const rightRange = Array.from(
        { length: 3 + 2 * siblingCount },
        (_, i) => totalPages - 3 - 2 * siblingCount + i + 1,
      );
      range.push(1, "ellipsis", ...rightRange);
    } else if (showLeftEllipsis && showRightEllipsis) {
      const middleRange = Array.from(
        { length: 2 * siblingCount + 1 },
        (_, i) => leftSiblingIndex + i,
      );
      range.push(1, "ellipsis", ...middleRange, "ellipsis", totalPages);
    } else {
      range.push(...Array.from({ length: totalPages }, (_, i) => i + 1));
    }

    return range;
  }, [currentPage, totalPages, siblingCount]);

  const buttonStyle: React.CSSProperties = {
    width: config.buttonSize,
    height: config.buttonSize,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: config.fontSize,
    fontFamily: tokens.typography.fontFamily.mono,
    fontWeight: tokens.typography.fontWeight.medium,
    backgroundColor: isTransparentTheme
      ? `${tokens.colors.background.surface}99`
      : "transparent",
    border: `1px solid ${tokens.colors.border.muted}`,
    borderRadius: tokens.borderRadius.md,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    transition: `all ${tokens.animation.fast}`,
    color: tokens.colors.text.primary,
    backdropFilter: isTransparentTheme ? "blur(8px)" : undefined,
    WebkitBackdropFilter: isTransparentTheme ? "blur(8px)" : undefined,
  };

  const activeButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: tokens.colors.accent.primary,
    borderColor: tokens.colors.accent.primary,
    color: tokens.colors.text.inverse,
    boxShadow: `0 0 0 1px ${tokens.colors.accent.primary}55, 0 0 14px ${tokens.colors.accent.primary}55`,
  };

  const handleClick = (page: number) => {
    if (!disabled && page >= 1 && page <= totalPages && page !== currentPage) {
      onChange(page);
    }
  };

  const NavButton = ({
    direction,
    targetPage,
    children,
  }: {
    direction: "first" | "prev" | "next" | "last";
    targetPage: number;
    children: React.ReactNode;
  }) => {
    const isDisabled =
      disabled ||
      (direction === "first" || direction === "prev"
        ? currentPage <= 1
        : currentPage >= totalPages);

    return (
      <button
        type="button"
        onClick={() => handleClick(targetPage)}
        disabled={isDisabled}
        aria-label={
          direction === "first"
            ? "Go to first page"
            : direction === "prev"
              ? "Go to previous page"
              : direction === "next"
                ? "Go to next page"
                : "Go to last page"
        }
        style={{
          ...buttonStyle,
          opacity: isDisabled ? 0.3 : 1,
          cursor: isDisabled ? "not-allowed" : "pointer",
        }}
      >
        {children}
      </button>
    );
  };

  return (
    <nav
      className={classNames("zendir-pagination", className)}
      aria-label="Pagination"
      style={{
        display: "flex",
        alignItems: "center",
        gap: tokens.spacing.xs,
        padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
        borderRadius: tokens.borderRadius.lg,
        ...(tokens.colors.border.cardStyle ?? {
          border: `1px solid ${tokens.colors.border.muted}`,
        }),
        background: isTransparentTheme
          ? `${tokens.colors.background.surface}66`
          : `${tokens.colors.background.surface}66`,
        backdropFilter: isTransparentTheme ? "blur(10px)" : undefined,
        WebkitBackdropFilter: isTransparentTheme ? "blur(10px)" : undefined,
      }}
    >
      {showFirstLast && (
        <NavButton direction="first" targetPage={1}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6zM6 6h2v12H6z" />
          </svg>
        </NavButton>
      )}

      {showPrevNext && (
        <NavButton direction="prev" targetPage={currentPage - 1}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </NavButton>
      )}

      {displayMode === "text" ? (
        <span
          style={{
            padding: `0 ${tokens.spacing.sm}`,
            color: tokens.colors.text.secondary,
            fontSize: tokens.typography.fontSize.xs,
            fontFamily: tokens.typography.fontFamily.mono,
            letterSpacing: "0.02em",
            whiteSpace: "nowrap",
          }}
        >
          Page {currentPage} of {totalPages}
        </span>
      ) : (
        pageNumbers.map((page, index) =>
          page === "ellipsis" ? (
            <span
              key={`ellipsis-${index}`}
              style={{
                width: config.buttonSize,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: tokens.colors.text.muted,
              }}
            >
              ...
            </span>
          ) : (
            <button
              key={page}
              type="button"
              onClick={() => handleClick(page)}
              disabled={disabled}
              aria-current={page === currentPage ? "page" : undefined}
              style={page === currentPage ? activeButtonStyle : buttonStyle}
              onMouseEnter={(e) => {
                if (!disabled && page !== currentPage) {
                  e.currentTarget.style.backgroundColor = `${tokens.colors.accent.primary}22`;
                  e.currentTarget.style.borderColor =
                    tokens.colors.accent.primary;
                  e.currentTarget.style.transform = "translateY(-1px)";
                }
              }}
              onMouseLeave={(e) => {
                if (!disabled && page !== currentPage) {
                  e.currentTarget.style.backgroundColor = isTransparentTheme
                    ? `${tokens.colors.background.surface}99`
                    : "transparent";
                  e.currentTarget.style.borderColor =
                    tokens.colors.border.muted;
                  e.currentTarget.style.transform = "translateY(0)";
                }
              }}
            >
              {page}
            </button>
          ),
        )
      )}

      {showPrevNext && (
        <NavButton direction="next" targetPage={currentPage + 1}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
        </NavButton>
      )}

      {showFirstLast && (
        <NavButton direction="last" targetPage={totalPages}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6zM16 6h2v12h-2z" />
          </svg>
        </NavButton>
      )}
    </nav>
  );
});

export default Pagination;
